import { Box, Paper, PaperProps, Typography } from '@material-ui/core'
import { Card, Condition } from 'components/facets'
import { CrossedOut } from 'components/facets/CrossedOut'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import { useUser } from 'components/providers/UserProvider'
import {
  AnyYesNo,
  CardConditionCondition,
  CardExtraAttribute,
  CardExtraCondition,
  CardLanguage,
  CardNameCondition,
  CommentCondition,
  ConditionEnum,
  ConditionTypesEnum,
  CountryEditionCondition,
  countryEditionMap,
  ExpansionCondition,
  FinishTypeCondition,
  FINISH_TYPE_LABELS,
  flagCode,
  LanguageCondition,
  PriceCondition,
  PriceGuideCondition,
  PricingPlanNodeCondition,
  QuantityCondition,
  RarityCondition,
} from 'shared'
import {
  COLD_FOIL_ICON,
  COSMOS_HOLO_ICON,
  FIRST_EDITION_ICON,
  FOIL_ICON,
  ICE_CRACKED_HOLO_ICON,
  MASTERBALL_HOLO_ICON,
  PLAYSET_ICON,
  POKEBALL_HOLO_ICON,
  RAINBOW_FOIL_ICON,
  REVERSE_COSMOS_ICON,
  REVERSE_HOLO_ICON,
  REVERSE_ICE_CRACKED_ICON,
  REVERSE_MASTERBALL_ICON,
  REVERSE_POKEBALL_ICON,
  REVERSE_STAMPED_ICON,
  SIGNED_ICON,
  STAMPED_HOLO_ICON,
} from 'utils/constants'

export interface ConditionPresenterProps {
  condition: PricingPlanNodeCondition
  paperProps?: PaperProps
  maxHeight?: number
  displayType?: boolean
}

const PriceGuideConditionPresenter = ({
  condition,
}: {
  condition: PriceGuideCondition
}): JSX.Element => {
  let text = ''

  if (typeof condition.value.min === 'number' && typeof condition.value.max === 'number')
    text = `${condition.value?.metric} between ${condition.value?.min} and ${condition.value?.max}`
  else if (typeof condition.value.min === 'number') {
    text = `${condition.value?.metric} above ${condition.value?.min}`
  } else if (typeof condition.value.max === 'number') {
    text = `${condition.value?.metric} below ${condition.value?.max}`
  }

  return <Typography variant="body2">{text}</Typography>
}
const PriceConditionPresenter = ({ condition }: { condition: PriceCondition }): JSX.Element => {
  let text = ''

  if (typeof condition.value.min === 'number' && typeof condition.value.max === 'number')
    text = `between ${condition.value?.min} and ${condition.value?.max}`
  else if (typeof condition.value.min === 'number') {
    text = `above ${condition.value?.min}`
  } else if (typeof condition.value.max === 'number') {
    text = `below ${condition.value?.max}`
  }

  return <Typography variant="body2">{text}</Typography>
}

const LanguageConditionPresenter = ({
  condition,
}: {
  condition: LanguageCondition
}): JSX.Element => {
  return (
    <>
      <Typography>
        {Array.isArray(condition.value) &&
          condition.value?.map((idLanguage) => (
            <LanguageFlag
              key={idLanguage}
              style={{ padding: '0px 5px' }}
              height="12"
              data-testid={`${CardLanguage[idLanguage]}-flag`}
              code={flagCode(idLanguage) || 'gg'}
            />
          ))}
      </Typography>
    </>
  )
}
const ExpansionConditionPresenter = ({
  condition,
}: {
  condition: ExpansionCondition
}): JSX.Element => {
  if (!Array.isArray(condition.value)) return <></>

  return (
    <>
      {condition.value?.map(({ name }) => (
        <Typography style={{ display: 'block' }} key={name} variant="caption">
          {name}
        </Typography>
      ))}
    </>
  )
}

const CardnameConditionPresenter = ({
  condition,
}: {
  condition: CardNameCondition
}): JSX.Element => {
  if (!Array.isArray(condition.value)) return <></>

  return (
    <>
      {condition.value?.map((card) => (
        <Typography
          key={card._id}
          component={'span'}
          style={{ display: 'inline-block', marginRight: '4px' }}
          variant="caption"
        >
          <Card key={card._id} card={card} />
        </Typography>
      ))}
    </>
  )
}

const CommentConditionPresenter = ({ condition }: { condition: CommentCondition }): JSX.Element => {
  if (typeof condition.value !== 'string') return <></>
  return <>{condition.value}</>
}

const extrasIcons = {
  [CardExtraAttribute.FirstEd]: FIRST_EDITION_ICON,
  [CardExtraAttribute.Signed]: SIGNED_ICON,
  [CardExtraAttribute.Playset]: PLAYSET_ICON,
}

const finishTypeIcons: Record<FINISH_TYPE_LABELS, string | null> = {
  [FINISH_TYPE_LABELS.FOIL]: FOIL_ICON,
  [FINISH_TYPE_LABELS.REVERSE_HOLO]: REVERSE_HOLO_ICON,
  [FINISH_TYPE_LABELS.REGULAR]: null,
  [FINISH_TYPE_LABELS.COLD_FOIL]: COLD_FOIL_ICON,
  [FINISH_TYPE_LABELS.RAINBOW_FOIL]: RAINBOW_FOIL_ICON,
  [FINISH_TYPE_LABELS.STAMPED_HOLO]: STAMPED_HOLO_ICON,
  [FINISH_TYPE_LABELS.ICE_CRACKED_HOLO]: ICE_CRACKED_HOLO_ICON,
  [FINISH_TYPE_LABELS.COSMOS_HOLO]: COSMOS_HOLO_ICON,
  [FINISH_TYPE_LABELS.REVERSE_COSMOS_HOLO]: REVERSE_COSMOS_ICON,
  [FINISH_TYPE_LABELS.MASTERBALL_HOLO]: MASTERBALL_HOLO_ICON,
  [FINISH_TYPE_LABELS.POKEBALL_HOLO]: POKEBALL_HOLO_ICON,
  [FINISH_TYPE_LABELS.REVERSE_STAMPED_HOLO]: REVERSE_STAMPED_ICON,
  [FINISH_TYPE_LABELS.REVERSE_MASTERBALL_HOLO]: REVERSE_MASTERBALL_ICON,
  [FINISH_TYPE_LABELS.REVERSE_POKEBALL_HOLO]: REVERSE_POKEBALL_ICON,
  [FINISH_TYPE_LABELS.REVERSE_ICE_CRACKED_HOLO]: REVERSE_ICE_CRACKED_ICON,
}

const FinishTypeConditionPresenter = ({ condition }: { condition: FinishTypeCondition }) => {
  const values = Array.isArray(condition.value) ? condition.value : []

  return (
    <Box display="flex">
      {values.map((finish) => {
        const icon = finishTypeIcons[finish as FINISH_TYPE_LABELS]
        return (
          <Box key={finish} paddingRight={1}>
            {icon ? (
              <img src={icon} alt={finish} style={{ width: '18px', height: '18px' }} />
            ) : (
              <Typography variant="caption">{finish}</Typography>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

const CountryEditionConditionPresenter = ({
  condition,
}: {
  condition: CountryEditionCondition
}) => {
  return (
    <Box display="flex" className="CountryEditionConditionPresenter">
      {condition.value.map((country) => {
        const flagCode = countryEditionMap.find((v) => v.value === country)?.label

        return (
          <Box key={country} paddingRight={1}>
            <LanguageFlag
              id={`${country}-flag`}
              //@ts-ignore
              height="15"
              code={flagCode}
            />
          </Box>
        )
      })}
    </Box>
  )
}

const CardExtraConditionPresenter = ({
  condition,
}: {
  condition: CardExtraCondition
}): JSX.Element => {
  return (
    <Box display="flex">
      {Object.keys(condition.value)
        .filter((attribute) => condition.value[attribute as CardExtraAttribute] !== AnyYesNo.Any)
        .map((attribute) => (
          <Box paddingRight={1} key={attribute as CardExtraAttribute}>
            {condition.value[attribute as CardExtraAttribute] ? (
              <img
                alt={attribute}
                src={extrasIcons[attribute as CardExtraAttribute]}
                style={{ width: '18px', height: '18px' }}
              />
            ) : (
              <CrossedOut>
                <img
                  alt={attribute}
                  src={extrasIcons[attribute as CardExtraAttribute]}
                  style={{ width: '18px', height: '18px' }}
                />
              </CrossedOut>
            )}
          </Box>
        ))}
    </Box>
  )
}

const CardConditionConditionPresenter = ({
  condition,
}: {
  condition: CardConditionCondition
}): JSX.Element => {
  if (condition.value.min === condition.value.max)
    return (
      <Typography>
        <Condition value={ConditionEnum[condition.value.min]} />
      </Typography>
    )
  else {
    return (
      <Typography>
        <Condition value={ConditionEnum[condition.value.min]} />
        -
        <Condition value={ConditionEnum[condition.value.max]} />
      </Typography>
    )
  }
}
const QuantityConditionPresenter = ({
  condition,
}: {
  condition: QuantityCondition
}): JSX.Element => {
  const { min, max } = condition.value
  // Both bounds are always applied, so both are always shown
  if (min === max) return <Typography>={min}</Typography>
  return (
    <Typography>
      {min}
      {' - '}
      {max}
    </Typography>
  )
}

const RarityConditionPresenter = ({ condition }: { condition: RarityCondition }): JSX.Element => {
  const { activeGame } = useUser()

  const numberOfRarities = Object.keys(activeGame.rarities).length

  if (condition.value.min === condition.value.max) {
    return <Typography>={activeGame.rarities[condition.value.min]?.name}</Typography>
  } else if (condition.value.min > 1 && condition.value.max < numberOfRarities)
    return (
      <Typography>
        {activeGame.rarities[condition.value.min]?.name}
        {' - '}
        {activeGame.rarities[condition.value.max]?.name}
      </Typography>
    )
  else if (condition.value.min > 1) {
    return (
      <Typography>
        {'>='}
        {activeGame.rarities[condition.value.min]?.name}
      </Typography>
    )
  } else if (condition.value.max < numberOfRarities) {
    return (
      <Typography>
        {'<='}
        {activeGame.rarities[condition.value.max]?.name}
      </Typography>
    )
  } else {
    return <Typography>...</Typography>
  }
}

export const ConditionPresenter = ({
  condition,
  paperProps,
  displayType = true,
}: ConditionPresenterProps): JSX.Element => {
  return (
    <Box maxHeight="inherit">
      <Paper elevation={2} {...paperProps}>
        <Box>
          {displayType && (
            <Box paddingLeft={0.5} paddingRight={0.5}>
              <Typography variant="overline">
                {condition.type} {condition.negated ? ' - not' : ''}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" marginTop="-8px">
            <Box
              height="3rem"
              display="flex"
              padding={1}
              paddingTop={displayType ? 0 : 1}
              overflow="auto"
              width="12rem"
            >
              <Box margin="auto" width="100%">
                {
                  {
                    [ConditionTypesEnum.PriceGuide]: (
                      <PriceGuideConditionPresenter condition={condition as PriceGuideCondition} />
                    ),
                    [ConditionTypesEnum.Printing]: (
                      <CardnameConditionPresenter condition={condition as CardNameCondition} />
                    ),
                    [ConditionTypesEnum.Expansion]: (
                      <ExpansionConditionPresenter condition={condition as ExpansionCondition} />
                    ),

                    [ConditionTypesEnum.Language]: (
                      <LanguageConditionPresenter condition={condition as LanguageCondition} />
                    ),
                    [ConditionTypesEnum.CardExtra]: (
                      <CardExtraConditionPresenter condition={condition as CardExtraCondition} />
                    ),
                    [ConditionTypesEnum.CardCondition]: (
                      <CardConditionConditionPresenter
                        condition={condition as CardConditionCondition}
                      />
                    ),
                    [ConditionTypesEnum.Comment]: (
                      <CommentConditionPresenter condition={condition as CommentCondition} />
                    ),
                    [ConditionTypesEnum.Quantity]: (
                      <QuantityConditionPresenter condition={condition as QuantityCondition} />
                    ),
                    [ConditionTypesEnum.Rarity]: (
                      <RarityConditionPresenter condition={condition as RarityCondition} />
                    ),
                    [ConditionTypesEnum.Price]: (
                      <PriceConditionPresenter condition={condition as PriceCondition} />
                    ),
                    [ConditionTypesEnum.FinishType]: (
                      <FinishTypeConditionPresenter condition={condition as FinishTypeCondition} />
                    ),
                    [ConditionTypesEnum.CountryEdition]: (
                      <CountryEditionConditionPresenter
                        condition={condition as CountryEditionCondition}
                      />
                    ),
                  }[condition.type]
                }
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
